import { auth } from '@/lib/firebase';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/api/v1';


async function getToken(): Promise<string> {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) throw new ApiError('Not authenticated', 401);
  return user.getIdToken();
}

class ApiError extends Error {
  status: number;
  data?: unknown;
  /**
   * v3.4 (25/05/26) — Machine-readable error code from the backend
   * `error.code` field, e.g. 'CONFLICT', 'INVITATION_TARGET_BELONGS_TO_OTHER_OWNER',
   * 'VALIDATION_ERROR'. Used by callers to render localized messages without
   * parsing the English `message` string.
   */
  code?: string;
  constructor(message: string, status: number, data?: unknown, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.code = code;
  }
}

/**
 * Per-request options. `silenceUpgradeWall` suppresses the global
 * `hiromatch:upgrade-wall` CustomEvent so callers that already render an
 * inline upgrade message (e.g. AI chat assistants on Job Position / Application
 * pages) don't trigger a duplicate modal on top of the chat hint.
 *
 * The `hiromatch:trial-state-changed` event is still emitted because the trial
 * banner needs to refresh regardless of who handles the 402.
 */
export interface ApiCallOptions {
  silenceUpgradeWall?: boolean;
  /** Optional endpoints: return this on 404 instead of throwing. */
  notFoundValue?: unknown;
}

async function handleResponse<T>(res: Response, opts?: ApiCallOptions): Promise<T> {
  if (res.status === 401) {
    // Could trigger logout here
    throw new ApiError('Unauthorized', 401);
  }
  if (res.status === 404 && opts?.notFoundValue !== undefined) {
    return opts.notFoundValue as T;
  }
  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    let errorData: unknown;
    let errorCode: string | undefined;
    try {
      const err = await res.json();
      // v3.4 (25/05/26) — capture the machine-readable error code so callers
      // can render localized messages from i18n instead of parsing the BE
      // English fallback. The BE error handler always sets `error: <CODE>`.
      if (typeof err.error === 'string') {
        errorCode = err.error;
      } else if (err.error && typeof err.error === 'object' && typeof err.error.code === 'string') {
        errorCode = err.error.code;
      }
      // Extract detailed validation issues if present
      if (err.metadata?.issues?.length) {
        message = err.metadata.issues.map((i: any) => `${i.path}: ${i.message}`).join('; ');
      } else {
        message = err.error?.message || err.message || (typeof err.error === 'string' ? err.error : undefined) || message;
      }
      // Preserve structured error payload (e.g. billing_limit_reached data).
      // Backend may wrap as { error: { data: {...} } } (v3) or top-level { data: {...} } (legacy).
      if (err.error && typeof err.error === 'object' && 'data' in err.error && err.error.data !== undefined) {
        errorData = err.error.data;
      } else if (err.data !== undefined) {
        errorData = err.data;
      }
    } catch {}
    // Trial expired interceptor — fire global event so UpgradeWallModal can open.
    if (res.status === 402 && errorData && typeof errorData === 'object') {
      const data = errorData as { reason?: string };
      if (data.reason === 'trial_expired' || data.reason === 'structural_limit' || data.reason === 'insufficient_balance' || data.reason === 'plan_required') {
        try {
          if (!opts?.silenceUpgradeWall) {
            window.dispatchEvent(new CustomEvent('hiromatch:upgrade-wall', { detail: data }));
          }
          // Always emit trial-state-changed so the banner stays in sync even
          // when callers handle the 402 inline.
          if (data.reason === 'trial_expired') {
            window.dispatchEvent(new CustomEvent('hiromatch:trial-state-changed'));
          }
        } catch { /* noop */ }
      }
    }
    throw new ApiError(message, res.status, errorData, errorCode);
  }
  // 204 No Content — no body to parse (DELETE endpoints)
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const json = await res.json();
  // Unwrap `{ data: payload }` only when `data` is present and non-null. Otherwise a body like
  // `{ checkoutUrl: "…", data: null }` would incorrectly yield `null` (because `null !== undefined`).
  if (json !== null && typeof json === 'object' && !Array.isArray(json) && 'data' in json) {
    const payload = (json as { data: unknown }).data;
    if (payload !== undefined && payload !== null) {
      return payload as T;
    }
  }
  return json as T;
}

export async function apiGet<T>(path: string, opts?: ApiCallOptions): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<T>(res, opts);
}

/** GET that reports 404 without throwing (optional / not-yet-deployed routes). */
export async function apiGetWithFound<T>(
  path: string,
  fallback: T,
  opts?: ApiCallOptions,
): Promise<{ value: T; found: boolean }> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) {
    return { value: fallback, found: false };
  }
  const value = await handleResponse<T>(res, opts);
  return { value, found: true };
}

/** GET that must not use cached responses (e.g. billing reconcile after Stripe portal). */
export async function apiGetNoStore<T>(path: string, opts?: ApiCallOptions): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
  return handleResponse<T>(res, opts);
}

export async function apiPost<T>(path: string, body?: unknown, opts?: ApiCallOptions): Promise<T> {
  const token = await getToken();
  // Sprint 4 (S4.C): when the caller passes a FormData payload (manual
  // application upload) we MUST NOT set Content-Type — the browser injects
  // the multipart boundary automatically. Stringifying FormData also breaks
  // the upload silently. Detection is structural so callers don't need any
  // opt-in flag.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (!isFormData) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: isFormData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined,
  });
  return handleResponse<T>(res, opts);
}

export async function apiPut<T>(path: string, body?: unknown, opts?: ApiCallOptions): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res, opts);
}

export async function apiPatch<T>(path: string, body?: unknown, opts?: ApiCallOptions): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res, opts);
}

export async function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ApiError(`File download failed: ${res.status}`, res.status);
  }
  return res.blob();
}

// ─── Public (no auth) ────────────────────────────────────────────────────────

export async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { cache: 'no-store' });
  return handleResponse<T>(res);
}

export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function submitCandidateApplication(formData: FormData): Promise<void> {
  const CANDIDATE_URL = import.meta.env.VITE_CANDIDATE_FUNCTION_URL as string || 'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/candidateApplication';
  const res = await fetch(`${CANDIDATE_URL}/apply`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let message = `Submission failed: ${res.status}`;
    try {
      const err = await res.json() as { message?: string; error?: string };
      message = err.message || err.error || message;
    } catch {}
    throw new ApiError(message, res.status);
  }
}

/**
 * apiStream — POST a streaming NDJSON endpoint and dispatch events to a callback.
 *
 * Used by api.ai.chatStream() to consume POST /ai/chat/stream.
 * Each line in the response body is parsed as JSON and passed to onEvent.
 *
 * Error handling:
 * - HTTP errors before the stream starts (401, 402, 403, 400, 500) → throws ApiError
 * - AbortError (signal.abort()) → silently ignored (not an error)
 * - Malformed JSON lines → silently skipped
 *
 * Sprint ai_chat_streaming (09/06/26).
 */
export async function apiStream<TEvent>(
  path: string,
  body: unknown,
  onEvent: (event: TEvent) => void,
  opts?: ApiCallOptions & { signal?: AbortSignal },
): Promise<void> {
  const token = await getToken();
  let res: Response;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: opts?.signal,
    });
  } catch (err) {
    // AbortError — silently ignore (user reset the chat)
    if (err instanceof Error && err.name === 'AbortError') return;
    throw err;
  }

  // HTTP error before stream starts — handle normally
  if (!res.ok) {
    await handleResponse<never>(res, opts);
    return; // handleResponse always throws on !res.ok, but TS needs this
  }

  if (!res.body) return;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      let done: boolean;
      let value: Uint8Array | undefined;

      try {
        ({ done, value } = await reader.read());
      } catch (err) {
        // AbortError mid-stream — silently ignore
        if (err instanceof Error && err.name === 'AbortError') return;
        throw err;
      }

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Last element may be incomplete — keep it in buffer
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const event = JSON.parse(trimmed) as TEvent;
          onEvent(event);
        } catch {
          // Malformed JSON line — skip silently
        }
      }
    }

    // Flush any remaining buffer content
    if (buffer.trim()) {
      try {
        const event = JSON.parse(buffer.trim()) as TEvent;
        onEvent(event);
      } catch {
        // Ignore malformed trailing data
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export { ApiError };
